import { useState } from 'react';
import { KLINIO_DEFAULT_ACCENT } from './BrandContext';
import { supabase } from './supabaseClient';
import { showToast } from './Toast';
import { NEUTRAL, STATUS_COLORS, resolveTheme } from './designTokens';
import DarkHeader, { darkPrimary } from './components/primitives/DarkHeader';
import Card from './components/primitives/Card';
import Button from './components/primitives/Button';
import FormInput from './components/primitives/FormInput';

function ThemedTextarea({ theme, label, value, onChange, placeholder, rows = 2 }) {
  return (
    <div style={{ marginBottom: '10px' }}>
      {label && (
        <label style={{ display: 'block', fontSize: '0.82em', fontWeight: '600', color: NEUTRAL.text, marginBottom: '4px' }}>{label}</label>
      )}
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        style={{
          width: '100%', padding: '9px 12px', borderRadius: '7px',
          border: `1px solid ${theme.card_border}`, fontSize: '0.9em',
          boxSizing: 'border-box', outline: 'none', resize: 'none',
          transition: 'border-color 0.12s', background: 'white',
        }}
      />
    </div>
  );
}

// ─── Inline-formulär: Skapa program ──────────────────────────────────────────
function CreateProgramForm({ therapistId, theme, onCancel, onCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) return;
    setSaving(true);
    const { error } = await supabase.from('programs').insert({
      therapist_id: therapistId,
      title: title.trim(),
      description: description.trim() || null,
    });
    setSaving(false);
    if (error) showToast('Kunde inte spara program: ' + error.message, 'error');
    else onCreated();
  };

  return (
    <Card theme={theme} style={{ padding: '16px', border: `1px solid ${theme.accent}`, boxShadow: `0 0 0 3px ${theme.accent}18` }}>
      <FormInput
        theme={theme}
        label="Programnamn"
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && save()}
        placeholder="T.ex. Axelrehab 8 veckor"
      />
      <ThemedTextarea
        theme={theme}
        label="Kort beskrivning (valfritt)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Kort beskrivning (valfritt)..."
        rows={2}
      />
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button theme={theme} variant="primary" onClick={save} disabled={!title.trim() || saving}>
          {saving ? 'Skapar...' : 'Skapa program'}
        </Button>
        <Button theme={theme} variant="secondary" onClick={onCancel}>Avbryt</Button>
      </div>
    </Card>
  );
}

// ─── Hjälp: beräkna status per program ────────────────────────────────────────
function getProgramStatus(program, blockCount) {
  if (blockCount === 0) return { type: 'draft', label: 'Utkast' };
  const cps = program.client_programs || [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const activeCount = cps.filter(cp => {
    if (!cp.end_date) return true;
    const [y, m, d] = cp.end_date.split('-').map(Number);
    return new Date(y, m - 1, d) >= today;
  }).length;
  if (activeCount > 0) return { type: 'active', label: 'Tilldelat', activeCount };
  return { type: 'inactive', label: 'Ej tilldelat', activeCount: 0 };
}

function getLatestAssignment(program) {
  const cps = program.client_programs || [];
  if (cps.length === 0) return null;
  const dates = cps.map(cp => cp.start_date).filter(Boolean).sort();
  if (dates.length === 0) return null;
  const latest = dates[dates.length - 1];
  const [y, m, d] = latest.split('-');
  const MONTHS_SV = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];
  const month = MONTHS_SV[parseInt(m, 10) - 1];
  const currentYear = new Date().getFullYear();
  if (parseInt(y, 10) === currentYear) return `${parseInt(d, 10)} ${month}`;
  return `${parseInt(d, 10)} ${month} ${y}`;
}

// ─── Programlista ─────────────────────────────────────────────────────────────
function ProgramList({
  programs,
  duplicating,
  showCreateForm,
  therapistId,
  accentColor = KLINIO_DEFAULT_ACCENT,
  onSelectProgram,
  onDuplicateProgram,
  onDeleteProgram,
  onShowCreateForm,
  onHideCreateForm,
  onProgramCreated,
}) {
  const theme = resolveTheme(accentColor);
  const [search, setSearch] = useState('');

  const getBlocks = (program) => Array.isArray(program.program_blocks) ? program.program_blocks : [];
  const getBlockCount = (program) => getBlocks(program).length;
  const getWeeks = (program) => { const b = getBlocks(program); return b.length > 0 ? Math.max(...b.map(x => x.end_week || 0)) : 0; };

  const filtered = programs.filter(p =>
    !search || p.title.toLowerCase().includes(search.toLowerCase())
  );
  const statuses = programs.map(p => getProgramStatus(p, getBlockCount(p)));
  const assignedCount = statuses.filter(st => st.type === 'active').length;
  const draftCount = statuses.filter(st => st.type === 'draft').length;

  const subtitle = [
    `${programs.length} program`,
    assignedCount > 0 ? <span key="a" style={{ color: theme.accent_soft_bg }}>{assignedCount} tilldelade</span> : null,
    draftCount > 0 ? `${draftCount} utkast` : null,
  ].filter(Boolean);

  const chip = (st) => {
    const c = st.type === 'active'
      ? { background: STATUS_COLORS.success_bg, color: '#15803D' }
      : st.type === 'draft'
        ? { background: STATUS_COLORS.warning_bg, color: '#B45309' }
        : { background: STATUS_COLORS.neutral_bg, color: STATUS_COLORS.neutral };
    return <span style={{ ...c, borderRadius: '999px', padding: '2px 8px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>{st.label}</span>;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <DarkHeader
        theme={theme}
        title="Program"
        subtitle={subtitle.map((p, i) => <span key={i}>{i > 0 && ' · '}{p}</span>)}
        actions={!showCreateForm && <button type="button" onClick={onShowCreateForm} style={darkPrimary(theme)}>+ Nytt program</button>}
        search={{ value: search, onChange: setSearch, placeholder: 'Sök program…' }}
      />

      {showCreateForm && (
        <CreateProgramForm
          therapistId={therapistId}
          theme={theme}
          onCancel={onHideCreateForm}
          onCreated={onProgramCreated}
        />
      )}

      <div style={{ background: theme.card_bg, border: `1px solid ${theme.card_border}`, borderRadius: theme.card_radius, boxShadow: theme.card_shadow, overflow: 'hidden' }}>
        {filtered.length === 0 ? (
          <div style={{ margin: '12px', padding: '24px', textAlign: 'center', borderRadius: '10px', border: `1px dashed ${theme.card_border}`, fontSize: '12px', color: NEUTRAL.muted }}>
            {programs.length === 0 ? 'Inga program ännu · klicka på + Nytt program' : 'Inga program matchar sökningen'}
          </div>
        ) : filtered.map((program, idx) => {
          const blockCount = getBlockCount(program);
          const weeks = getWeeks(program);
          const status = getProgramStatus(program, blockCount);
          const latestDate = getLatestAssignment(program);
          const isDraft = status.type === 'draft';
          const meta = isDraft
            ? 'Inga faser ännu'
            : [
                `${blockCount} ${blockCount === 1 ? 'fas' : 'faser'}`,
                weeks > 0 ? `${weeks} veckor` : null,
                status.activeCount > 0 ? `${status.activeCount} ${status.activeCount === 1 ? 'kund' : 'kunder'}` : latestDate ? `senast tilldelat ${latestDate}` : null,
              ].filter(Boolean).join(' · ');
          return (
            <div
              key={program.id}
              onClick={() => onSelectProgram(program)}
              style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 20px', borderBottom: idx < filtered.length - 1 ? `1px solid ${theme.card_border}` : 'none', cursor: 'pointer', opacity: isDraft ? 0.75 : 1 }}
            >
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: '800', background: isDraft ? 'transparent' : theme.accent_soft_bg, color: isDraft ? NEUTRAL.faint : theme.accent_soft_text, border: isDraft ? `1px dashed ${NEUTRAL.line}` : 'none' }}>
                {blockCount}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: NEUTRAL.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{program.title}</div>
                <div style={{ fontSize: '12px', color: NEUTRAL.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meta}</div>
              </div>
              {chip(status)}
              <span aria-hidden="true" style={{ fontSize: '14px', color: NEUTRAL.line }}>›</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ProgramList;
